import React from 'react';
import { useTranslation } from 'react-i18next';

const rpeBand = (v) => {
  if (v <= 2) return '1_2';
  if (v <= 4) return '3_4';
  if (v <= 6) return '5_6';
  if (v <= 8) return '7_8';
  return '9_10';
};

const rpeColor = (v) => {
  if (v <= 2) return 'bg-emerald-500 border-emerald-500';
  if (v <= 4) return 'bg-green-500 border-green-500';
  if (v <= 6) return 'bg-amber-500 border-amber-500';
  if (v <= 8) return 'bg-orange-500 border-orange-500';
  return 'bg-red-600 border-red-600';
};

export function rpeLabel(t, value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v < 1) return null;
  return t(`feedback.rpe_band.${rpeBand(v)}`);
}

/**
 * RPE (1-10) selector + pain toggle / location. Controlled component.
 * value: { rpe: number|null, pain: boolean|null, pain_location: string }
 */
export default function RpePainFields({ value, onChange, disabled = false }) {
  const { t } = useTranslation();
  const rpe = value?.rpe ?? null;
  const pain = value?.pain ?? null;
  const painLocation = value?.pain_location || '';
  const set = (patch) => onChange?.({ rpe, pain, pain_location: painLocation, ...patch });

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <label className="block text-xs font-black text-gray-500 uppercase tracking-widest">
            {t('feedback.rpe_label')} <span className="normal-case tracking-normal text-gray-400 font-bold">({t('workout.optional')})</span>
          </label>
          <span className="text-xs font-bold text-gray-600">{rpe ? `${rpe}/10 · ${rpeLabel(t, rpe)}` : ''}</span>
        </div>
        <div className="grid grid-cols-10 gap-1">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const active = rpe === n;
            return (
              <button
                key={n}
                type="button"
                disabled={disabled}
                onClick={() => set({ rpe: active ? null : n })}
                className={[
                  'h-9 rounded-lg border text-sm font-black tabular-nums transition-all',
                  active ? `${rpeColor(n)} text-white shadow-md scale-105` : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                  disabled ? 'opacity-60 cursor-not-allowed' : ''
                ].join(' ')}
                aria-pressed={active}
                aria-label={`RPE ${n}`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-wide">
          <span>{t('feedback.rpe_band.1_2')}</span>
          <span>{t('feedback.rpe_band.9_10')}</span>
        </div>
      </div>

      <div>
        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
          {t('feedback.pain_label')}
        </label>
        <div className="flex gap-2">
          {[
            { v: false, label: t('feedback.pain_no') },
            { v: true, label: t('feedback.pain_yes') }
          ].map((opt) => {
            const active = pain === opt.v;
            return (
              <button
                key={String(opt.v)}
                type="button"
                disabled={disabled}
                onClick={() => set({ pain: active ? null : opt.v })}
                className={[
                  'flex-1 py-2.5 rounded-xl border-2 text-sm font-bold transition-all',
                  active
                    ? (opt.v ? 'border-red-500 bg-red-50 text-red-700' : 'border-emerald-500 bg-emerald-50 text-emerald-700')
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                  disabled ? 'opacity-60 cursor-not-allowed' : ''
                ].join(' ')}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {pain === true ? (
          <input
            type="text"
            disabled={disabled}
            maxLength={120}
            value={painLocation}
            onChange={(e) => set({ pain_location: e.target.value })}
            placeholder={t('feedback.pain_location_placeholder')}
            className="mt-3 w-full border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none text-sm font-medium"
          />
        ) : null}
      </div>
    </div>
  );
}
